import { useSelector } from 'react-redux'
import { Bar, Cell } from 'recharts'
import { CalculateSize } from '../../../../../../../common/CalculateSize'
import { ChartContainer } from '../../../../../../../common/chart/chartContainer'
import { MARGIN_CHART } from '../../../../../../../common/chart/chartContainer/constants'
import { Legend } from '../../../../../../../common/chart/legend'
import { Loading } from '../../../../../../../common/loading'
import { Notification } from '../../../../../../../common/notification'
import { SCREEN } from '../../../../../../../common/selectCurrencyUnit/constant'
import { useFormatCurrency } from '../../../../../../../common/selectCurrencyUnit/useFormatCurrency'
import {
  selectIsLoading,
  selectRawData,
  selectSortBy,
  selectYear,
} from '../../store/slice'

export const Content = ({ yAxisWidth, height }) => {
  const { formatedUnit, formatCurrency, formatCurrencyWithUnit } =
    useFormatCurrency(SCREEN.INDUSTRY_OVERVIEW)

  const data = useSelector(selectRawData)
  const isLoading = useSelector(selectIsLoading)
  const year = useSelector(selectYear)
  const sortChart = useSelector(selectSortBy)

  const chartData = data
    .filter((item) => item.year === year)
    .map((item) => ({
      name: item.vsicName,
      import: item.import,
      export: item.export,
      balanceOfTrade: item.balanceOfTrade,
      isMain: item.isMain,
    }))
    .sort((a, b) => {
      switch (sortChart.value) {
        case 'IMPORT_DESCENDING':
          if (a.import < b.import) return 1
          if (a.import > b.import) return -1
          return 0
        case 'IMPORT_ASCENDING':
          if (a.import > b.import) return 1
          if (a.import < b.import) return -1
          return 0
        case 'EXPORT_DESCENDING':
          if (a.export < b.export) return 1
          if (a.export > b.export) return -1
          return 0
        case 'EXORT_ASCENDING':
          if (a.export > b.export) return 1
          if (a.export < b.export) return -1
          return 0
        case 'BALANCE_TRADE_DESCENDING':
          if (a.balanceOfTrade < b.balanceOfTrade) return 1
          if (a.balanceOfTrade > b.balanceOfTrade) return -1
          return 0
        case 'BALANCE_TRADE_ASCENDING':
          if (a.balanceOfTrade > b.balanceOfTrade) return 1
          if (a.balanceOfTrade < b.balanceOfTrade) return -1
          return 0
        case 'A->Z':
          if (a.name > b.name) return 1
          if (a.name < b.name) return -1
          return 0
        case 'Z->A':
          if (a.name < b.name) return 1
          if (a.name > b.name) return -1
          return 0
        default:
          return 0
      }
    })

  if (isLoading) {
    return (
      <div className="h100 positionRelative">
        <Loading />
      </div>
    )
  }

  if (!chartData.length) {
    return <Notification />
  }

  return (
    <div className="flexColumn h100">
      <div className="flex1 positionRelative">
        <CalculateSize height="100%">
          {(size) => {
            return (
              <ChartContainer
                margin={{ ...MARGIN_CHART, right: 50, bottom: 0 }}
                data={chartData}
                width={'100%'}
                height={height || size.height}
                layout="vertical"
                cartesianGrid={{ vertical: true, horizontal: false }}
                yAxis={[
                  {
                    dataKey: 'name',
                    yAxisId: 'left',
                    type: 'category',
                    width: yAxisWidth || 200,
                    tickStyle: (name) => {
                      const item =
                        chartData.find((item) => item.name === name) || {}

                      return {
                        color: item.isMain ? '#006AA6' : '#3A414B',
                        fontWeight: item.isMain ? 600 : 400,
                      }
                    },
                    tickFormatter: (name) => name,
                    lineClamp: 1,
                  },
                ]}
                xAxis={[
                  {
                    type: 'number',
                    dataKeys: ['import', 'export', 'balanceOfTrade'],
                    axisLine: true,
                    tickFormatter: (value) =>
                      formatCurrency({
                        value,
                        year,
                        exchangeRateType: 'averageRate',
                      }),
                  },
                ]}
                tooltip={{
                  maxWidth: 300,
                  schema: {
                    content: [
                      {
                        dataKey: 'import',
                        label: 'industryOverview.macroeconomics.IMPORT',
                        isI18n: true,
                        formatValue: (value) =>
                          formatCurrencyWithUnit({
                            value,
                            year,
                            exchangeRateType: 'averageRate',
                          }),
                      },
                      {
                        dataKey: 'export',
                        label: 'industryOverview.macroeconomics.EXPORT',
                        isI18n: true,
                        formatValue: (value) =>
                          formatCurrencyWithUnit({
                            value,
                            year,
                            exchangeRateType: 'averageRate',
                          }),
                      },
                      {
                        dataKey: 'balanceOfTrade',
                        label:
                          'industryOverview.macroeconomics.BALANCE_OF_TRADE',
                        isI18n: true,
                        formatValue: (value) =>
                          formatCurrencyWithUnit({
                            value,
                            year,
                            exchangeRateType: 'averageRate',
                          }),
                      },
                    ],
                  },
                }}
              >
                <Bar
                  yAxisId={'left'}
                  dataKey={'import'}
                  barSize={10}
                  fill="#7FB4D2"
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.isMain ? '#006AA6' : '#7FB4D2'}
                    />
                  ))}
                </Bar>
                <Bar
                  yAxisId={'left'}
                  dataKey={'export'}
                  barSize={10}
                  fill="#D9E5C2"
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.isMain ? '#B3CC85' : '#D9E5C2'}
                    />
                  ))}
                </Bar>
                <Bar
                  yAxisId={'left'}
                  dataKey={'balanceOfTrade'}
                  barSize={10}
                  fill="#FFE6AF"
                >
                  {chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={entry.isMain ? '#FFCD60' : '#FFE6AF'}
                    />
                  ))}
                </Bar>
              </ChartContainer>
            )
          }}
        </CalculateSize>
      </div>
      <div className="dFlex justifyContentEnd">{formatedUnit}</div>
      <Legend
        className="dFlex justifyContentCenter gap2"
        items={[
          {
            key: 1,
            color: '#006AA6',
            title: 'industryOverview.macroeconomics.IMPORT',
          },
          {
            key: 2,
            color: '#B3CC85',
            title: 'industryOverview.macroeconomics.EXPORT',
          },
          {
            key: 3,
            color: '#FFCD60',
            title: 'industryOverview.macroeconomics.BALANCE_OF_TRADE',
          },
        ]}
      />
    </div>
  )
}
